import { DrawerParams } from "@/constants/drawer";
import { Document, FileDataProps } from "@/services/api";
import { selectRequestFormStore, useBoundStore } from "@/services/stores";
import { getBase64, saveBase64ToFile } from "@/utils/get-base64";
import { getFileSizeFromBase64 } from "@/utils/get-file-size-from-base64";
import { map } from "lodash";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { getBooleanConfigValue } from "@/utils";
import { appConfig } from "@/guards/auth/azure-b2c.config";

export const useOperationsWithAttachments = ({
  data,
  onSetIsValidStep,
  onSetIsLockStepWithLoader,
}: {
  data?: Document[] | null;
  onSetIsValidStep: (isValid: boolean) => void;
  onSetIsLockStepWithLoader: (isLocked: boolean) => void;
}) => {
  const {
    requestFormData,
    setRequestFormDataDocs,
    removeRequestFormDataDocs,
    setRequestFormData,
    documentsInitialized,
    setDocumentsInitialized,
  } = useBoundStore(selectRequestFormStore);

  const [searchParams, setSearchParams] = useSearchParams();
  const [isInit, setIsInit] = useState(false);

  const {
    clearErrors,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  if (getBooleanConfigValue(appConfig?.FEATURES?.IS_DEBUG_ENABLED)) {
    /* eslint-disable */
    console.log("AttachmentsForm", errors);
    /* eslint-enable */
  }

  /**
   * Edit mode hook
   */
  useEffect(() => {
    if (!data || !documentsInitialized) {
      onSetIsLockStepWithLoader(true);
    }

    if (data && !documentsInitialized) {
      map(data, doc => {
        const fileName = doc?.FileName ?? "";

        const fileType =
          `application/${doc?.Format?.split(".")?.[1] || doc?.Format}` ?? ""; // TODO clarify contract

        const fileSize = getFileSizeFromBase64(doc?.FileBase64);

        const file = {
          ...doc,
          type: fileType,
          name: fileName,
          size: fileSize,
          base64: doc?.FileBase64,
        };

        if (
          doc?.FileBase64 &&
          doc.DocTypeId &&
          !requestFormData?.RequestDocuments?.[doc.DocTypeId]
        ) {
          setValue(`${doc.DocTypeId}`, file);
          setRequestFormDataDocs(`${doc.DocTypeId}`, file);
        }
      });

      setDocumentsInitialized(true);
      onSetIsLockStepWithLoader(false);
      trigger();
    }
  }, [
    data,
    requestFormData?.RequestDocuments,
    setRequestFormData,
    setRequestFormDataDocs,
    setValue,
    trigger,
    documentsInitialized,
    setDocumentsInitialized,
    onSetIsValidStep,
    onSetIsLockStepWithLoader,
  ]);

  useEffect(() => {
    if (isInit) {
      return;
    }

    setIsInit(true);
    trigger();
  }, [isInit, trigger]);

  const handleUploadDocument = useCallback(
    (e: ChangeEvent<HTMLInputElement>, doc: Document) => {
      clearErrors();
      const file = e.target.files?.[0] ?? null;
      const fileName = file?.name ?? "";
      const fileType = file?.type ?? "";
      const fileSize = file?.size ?? 0;

      if (file) {
        getBase64(file, async base64 => {
          if (base64) {
            const _file = {
              base64,
              type: fileType,
              name: fileName,
              size: fileSize,
            };

            setValue(`${doc.DocTypeId}`, _file);
            setValue("RequestDocuments", true);
            setRequestFormDataDocs(`${doc.DocTypeId}`, {
              ..._file,
              ...doc,
            });
          }

          await trigger();
        });
      }
    },
    [clearErrors, setRequestFormDataDocs, setValue, trigger],
  );

  const handleRemoveDocument = useCallback(
    (docTypeId: string) => {
      setValue(docTypeId, null);
      removeRequestFormDataDocs(docTypeId);
      trigger();
    },
    [removeRequestFormDataDocs, setValue, trigger],
  );

  const handleDownloadDocument = useCallback(
    (file?: FileDataProps, isTemplate?: boolean) => {
      if (file) {
        saveBase64ToFile(file, isTemplate); // file.name, file.base64 as string
      }
    },
    [],
  );

  const handlePreviewDocument = useCallback(
    (file?: FileDataProps) => {
      const newQueryParameters: URLSearchParams = new URLSearchParams(
        searchParams,
      );

      newQueryParameters.set(DrawerParams.DRAWER_SEARCH_PARAM, `${file?.name}`);
      newQueryParameters.set(DrawerParams.DRAWER_DOC_ID, `${file?.DocTypeId}`);

      setSearchParams(newQueryParameters);
    },
    [searchParams, setSearchParams],
  );

  return {
    onDownloadDocument: handleDownloadDocument,
    onUploadDocument: handleUploadDocument,
    onRemoveDocument: handleRemoveDocument,
    onPreviewDocument: handlePreviewDocument,
  };
};
